import { forwardRef } from 'react';
import PlanCard from './PlanCard.jsx';
import CTA from './CTA.jsx';
import { IconGlyph } from '../../ui/Primitives.jsx';
import { t, currentLanguage } from '../../../i18n/strings.js';
import { formatMoney } from '../../../lib/pricing.js';
import { fillTemplate } from '../../../lib/helpers.js';

// Seletor de planos. As DUAS instâncias da página recebem o MESMO `plans`,
// `selectedId`, `onSelect`, `timerLabel` e `onCheckout` do PaywallIntl —
// não existe estado local de seleção aqui.
const PlanSelector = forwardRef(function PlanSelector(
  { title, subtitle, plans, status, selectedId, onSelect, timerLabel, onCheckout, onRetry },
  ref,
) {
  const selected = plans.find((p) => p.id === selectedId) ?? plans[0];
  const hasCoupon = plans.some((p) => p.discountPct);
  const couponPct = plans.find((p) => p.discountPct)?.discountPct;

  const renewal = selected
    ? fillTemplate(t(selected.promoAmount != null ? 'paywallIntl.renewal' : 'paywallIntl.renewalNoPromo'), {
        promo: selected.promoAmount != null ? formatMoney(selected.promoAmount, selected.currency, currentLanguage) : '',
        full: formatMoney(selected.amount, selected.currency, currentLanguage),
        period: t(`paywallIntl.period_${selected.interval}`),
      })
    : '';

  return (
    <div ref={ref} className="scroll-mt-20">
      {title && <h2 className="font-display font-extrabold text-[28px] leading-tight text-center">{title}</h2>}
      {subtitle && <p className="text-[15px] text-ink-muted text-center mt-2">{subtitle}</p>}

      {hasCoupon && (
        <div className="rounded-[20px] bg-surface-2 mt-4 p-1">
          <div className="flex items-center justify-between bg-cyan/15 rounded-2xl px-4 py-2.5">
            <div className="flex items-center gap-2">
              <IconGlyph name="ti-sparkles" size={16} className="text-cyan" />
              <span className="font-display font-bold text-sm">{t('paywallIntl.couponLabel')}</span>
            </div>
            <span className="font-display font-extrabold text-sm text-cyan">{couponPct}% OFF</span>
          </div>
          <div className="text-center text-xs text-ink-muted py-2">
            {t('paywallIntl.couponActiveFor')} <span className="font-bold text-ink">{timerLabel}</span>
          </div>
        </div>
      )}

      {status === 'loading' && (
        <div className="flex flex-col gap-3 mt-3" aria-busy="true" aria-label={t('paywallIntl.plansLoading')}>
          {[0, 1].map((i) => (
            <div key={i} className="h-[92px] rounded-[20px] bg-surface animate-pulse" />
          ))}
        </div>
      )}

      {status !== 'loading' && plans.length === 0 && (
        <div className="mt-3 rounded-[20px] bg-surface p-4 text-center">
          <p className="text-[14px] text-ink-muted">{t('paywallIntl.plansError')}</p>
          <button onClick={onRetry} className="mt-2 font-display font-bold text-cyan text-[14px]">
            {t('paywallIntl.plansRetry')}
          </button>
        </div>
      )}

      {plans.length > 0 && (
        <div className="flex flex-col gap-3 mt-3" role="radiogroup">
          {plans.map((p) => (
            <PlanCard key={p.id} plan={p} selected={p.id === selected?.id} onSelect={() => onSelect(p.id)} />
          ))}
        </div>
      )}

      <div className="mt-4">
        <CTA onClick={() => onCheckout(selected)} disabled={!selected}>
          {t('paywallIntl.unlockCta')}
        </CTA>
      </div>
      {selected && <p className="text-center text-[12.5px] text-ink-muted mt-3 leading-snug">{renewal}</p>}
    </div>
  );
});

export default PlanSelector;
